import History from './history'
import Engine from './engine'
import Modifier from './modifier'
import Field from '../mechanism/field'
import Type from '../mechanism/type'

export default class Character {
  constructor (playbook) {
    Object.assign(this, {
      playbook,
      rulebooks: playbook.rulebooks,
      choices: {},
      modifiers: [],
      fields: {},
      playbookFields: {}, // A calculated version of this.playbook.fields
      notes: '',
      history: new History(),
      listeners: [],
      alive: true
    })
    this.history.mount(this)
    Object.assign(this, Engine)
  }

  // Calculating all fields after character creation, registering all hooks
  async initialize (ui) {
    for(const field of this.playbook.rules.characterFields.filter(field => field instanceof Field.ManualField)) {
      this.fields[field.name] = await this.get(field.initializationFormula, { ui })
    }

    for (const hook of (this.playbook.rules.hooks || [])) {
      this.on(hook.trigger, async params => {
        await this.execute(hook.procedure, ui, params)
      })
    }

    await this._refresh()
    this.history.clear()
    await this._emit('initialization:over')
    console.log('-done-')
  }

  // Field procedures
  async die () {
    this.alive = false
    await this._emit('death')
  }

  async revive () {
    this.alive = true
    await this._emit('revival')
  }

  async set (field, value) {
    await this._change(() => {
      this.fields[field] = value
    })
  }

  async addToField (field, value) {
    await this._change(() => {
      if (Array.isArray(this.fields[field])) {
        this.fields[field].push(value)
      } else {
        this.fields[field] += Number(value)
      }
    })
  }

  async removeFromField (field, value) {
    await this._change(() => {
      if (Array.isArray(this.fields[field])) {
        this.fields[field].value.splice(field.value.indexOf(value))
      } else {
        this.fields[field] -= Number(value)
      }
    })
  }

  async toggle (field) {
    await this._change(() => {
      this.fields[field] = !this.fields[field]
    })
  }

  async addModifier (modifierParams) {
    await this._change(() => {
      this.modifiers.push(new Modifier(modifierParams))
    })
  }

  async _change (callback) {
    await this._emit('change:start')
    await callback()
    await this._emit('change:refreshing')
    await this._refresh()
    await this._emit('change:over')
  }

  async _refresh () {
    for(const field of this.playbook.rules.characterFields.filter(field => field instanceof Field.AutomaticField)) {
      this.fields[field.name] = await this.get(field.calculationFormula, {})
    }
    for(const field of this.playbook.rules.playbookFields) {
      if (field.type.name === 'formula') {
        this.playbookFields[`${field.name}-formula`] = this.playbook.fields[field.name]
      }
      this.playbookFields[field.name] = await this.get(field.name, {})
    }
  }

  // Event handling
  async _emit (event, params) {
    for (const listener of this.listeners.filter(listener  => listener.trigger === event)) {
      await listener.callback(params)
    }
  }

  on (trigger, callback) {
    this.listeners.push({ trigger, callback })
  }

  // Serialization
  toJson () {
    return Character.serialize(this)
  }

  static deserialize (json, rulebook) {
    const playbook = rulebook.playbooks.find(playbook => playbook.name === json.playbook)
    const character = new Character(playbook)
    character._refresh()

    Object.assign(character, {
      choices: json.choices,
      fields: json.fields,
      alive: json.alive,
      modifiers: json.modifiers.map(modifier => new Modifier(modifier)),
      notes: json.notes,
      history: History.deserialize(json.history)
    })

    return character
  }

  static serialize (character) {
    return {
      rulebooks: character.rulebooks,
      playbook: character.playbook.name,
      choices: character.choices,
      fields: character.fields,
      alive: character.alive,
      modifiers: character.modifiers.map(modifier => modifier.toJson()),
      notes: character.notes,
      history: character.history.toJson()
    }
  }
}

/*
* About UI:
* The UI parameter is an interface with the user and has the following functions:
* *
* * async output(text, title)             shows a text to the user.
* *                                       resolves when the user closes the window.
* *
* * async input(text, type=string)        shows a text to the user and accepts an input in response.
* *                                       resolves with the value from the user.
* *
* * async choose(text, options, count=1)  shows a text to the user and accepts a choice.
* *                                       resolves with the chosen value.
*/
